import { changeMode, isZoomingSelector, modeSelector } from 'features/map/mapSlice'
import { useEventSlug, useSlug } from 'hooks'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import routes from 'routes'
import styled, { css } from 'styled-components'
import { colors } from 'utils'
import Icon from '../../../components/Icon'
import { IconProps } from '../../../components/Icon/Icon'
import { IconOption } from '../../../components/Icon/icons'
import imageMapCreator from '../lib/imagemap-creator'
import { Tool as ToolType } from '../lib/imagemap-creator/p5.image-map-creator'

const S = {
    MapControls: styled.div`
    `,
    ControlWrapper: styled.div`
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        background: rgba(45, 48, 53, 0.9);
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
        overflow: hidden;
    `,
    Tool: styled.div<{ isActive: boolean, disabled?: boolean }>`
        & > span {
            position: relative;
            & > svg > path {
                fill: ${({ disabled }) => disabled && 'white !important'};
            }
            ${({ disabled }) => disabled && css`
                &::after {
                    content: '';
                    z-index: 0;
                    position: absolute;
                    top: 14px;
                    left: 1px;
                    width: 23px;
                    height: 2px;
                    border-radius: 3px;
                    background-color: white;
                    transform: rotate(-45deg);
                }
                &:hover:after {
                    background-color: filter: brightness(0.6);
                }
            `}
        }
        display: flex;
        justify-content: center;
        width: 44px;
        height: 48px;
        border: 3px solid transparent;
        border-right-color: ${({ isActive }) => (isActive ? colors.activeTool : 'transparent')};
        background-color: ${({ isActive }) => (isActive ? 'rgba(255, 255, 255, 0.25)' : 'transparent')};
    `
}

const Tool = (props: IconProps & { disabled?: boolean }) => (
    <S.Tool isActive={props.isActive} disabled={props.disabled}>
        <Icon size={24} color="white" style={{ margin: 6 }} {...props} />
    </S.Tool>
)

const MapControls = ({
    setTool,
    zoom,
}: ReturnType<typeof imageMapCreator>) => {
    const eventSlug = useEventSlug()
    const mapSlug = useSlug('mapSlug')
    const dispatch = useDispatch()
    const history = useHistory()
    const [currentTool, setCurrentTool] = React.useState('cursor')
    const isZooming = useSelector(isZoomingSelector)
    const mode = useSelector(modeSelector)

    const createToolClick = (tool: ToolType) => () => {
        setCurrentTool(tool)
        setTool(tool)
        if (tool === 'cursor') {
            dispatch(changeMode('viewing'))
        } else {
            dispatch(changeMode('editing'))
        }
    }
    const turnOffZoom = () => {
        zoom(-10)
        setCurrentTool('cursor')
        setTool('cursor')
        dispatch(changeMode('viewing'))
    }
    const isActive = (tool) => tool === currentTool
    const tools = [
        'polygon',
        'rectangle',
        'select',
    ]

    useEffect(() => () => {
        setTool('cursor')
        dispatch(changeMode('viewing'))
    }, [dispatch, setTool])

    useEffect(() => {
        if (mode === 'viewing') {
            setCurrentTool('cursor')
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode])

    return (
        <S.MapControls>
            <S.ControlWrapper>
                <Tool
                    icon="cog"
                    onClick={() => history.push(routes.mapSetting.getPath({ eventSlug, mapSlug }))}
                />
            </S.ControlWrapper>
            <S.ControlWrapper>
                <Tool
                    icon={!isZooming ? 'cursor' : 'zoomIn'}
                    isActive={!isZooming && isActive('cursor')}
                    onClick={isZooming ? turnOffZoom : createToolClick('cursor')}
                    disabled={isZooming}
                />
                {tools.map((tool: ToolType) => (
                    <Tool
                        key={tool}
                        icon={tool as IconOption}
                        isActive={isActive(tool)}
                        onClick={createToolClick(tool)}
                    />
                ))}
            </S.ControlWrapper>
        </S.MapControls>
    )
}

export default MapControls
